import { Controller } from '@hotwired/stimulus';
import consumer from '../channels/consumer';

export default class extends Controller {
  static targets = ['rooms']

  static values = {
    roomId: { type: String, default: null },
    everyoneLabel: { type: String, default: "Everyone" },
  }

  static ACTION_TYPES = {
    CREATE_ROOM: 'create_room',
    MOVE_ROOM_TO_TOP: 'move_room_to_top',
    UPDATE_ROOM_NAME: 'update_room_name',
    UPDATE_ROOM_LOGO: 'update_room_logo',
    DELETE_ROOM: 'delete_room',
    MARK_ROOM_AS_READ: 'mark_room_as_read',
    CREATE_MESSAGE: 'create_user_message',
    UPDATE_MESSAGE: 'update_user_message',
    DELETE_MESSAGE: 'delete_user_message'
  };

  static CLASSES = {
    ROOM: 'room',
    ROOM_ACTIVE: 'room--active',
    ROOM_UNREAD: 'room--has-unread-messages',
    ROOM_LOGO: 'room__logo',
    ROOM_NAME: 'room__name',
    ROOM_TITLE: 'room__title',
    ROOM_LAST_MESSAGE: 'room__last_message',
    ROOM_LAST_MESSAGE_TIME: 'room__last_message_time',
    ROOM_UNREAD_MARKER: 'room__unread_marker'
  };

  static IDS = {
    ROOM_CONTAINER: 'room'
  };

  currentRoomId = null;
  subscription = null;

  connect() {
    this.currentRoomId = this.roomIdValue;
    if (!this.subscription) {
      this._createSubscription();
    }
  }

  disconnect() {
    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription);
    }
  }

  _createSubscription() {
    this.subscription = consumer.subscriptions.create(
      { channel: 'RoomsChannel' },
      {
        connected: this._connected.bind(this),
        disconnected: this._disconnected.bind(this),
        received: this._received.bind(this)
      }
    );
  }

  _connected() {
  }

  _disconnected() {
  }

  _received(data) {
    if (!data?.payload) return;

    const actionHandlers = {
      [this.constructor.ACTION_TYPES.CREATE_ROOM]: this._createRoom,
      [this.constructor.ACTION_TYPES.MOVE_ROOM_TO_TOP]: this._moveRoomToTop,
      [this.constructor.ACTION_TYPES.UPDATE_ROOM_NAME]: this._updateRoomName,
      [this.constructor.ACTION_TYPES.UPDATE_ROOM_LOGO]: this._updateRoomLogo,
      [this.constructor.ACTION_TYPES.DELETE_ROOM]: this._deleteRoom,
      [this.constructor.ACTION_TYPES.MARK_ROOM_AS_READ]: this._markRoomAsRead,
      [this.constructor.ACTION_TYPES.CREATE_MESSAGE]: this._createUserMessage,
      [this.constructor.ACTION_TYPES.UPDATE_MESSAGE]: this._updateUserMessage,
      [this.constructor.ACTION_TYPES.DELETE_MESSAGE]: this._deleteUserMessage
    };

    const handler = actionHandlers[data.payload.type];
    if (handler) {
      handler.call(this, data.payload);
    }
  }

  _createRoom(payload) {
    this._clearAllActiveRooms();

    const roomHTML = this._buildRoomHTML(payload);
    this.roomsTarget.insertAdjacentHTML('afterbegin', roomHTML);

    this._setCurrentRoomId(payload.id);
  }

  _moveRoomToTop(payload) {
    let room = this._findRoom(payload.id);

    if (!room) {
      const roomData = {
        id: payload.id,
        name: payload.name,
        backgroundColor: '#cccccc',
        logoUrl: payload.logoUrl,
        selected: true,
        unread: false
      };

      const roomHTML = this._buildRoomHTML(roomData);
      this.roomsTarget.insertAdjacentHTML('afterbegin', roomHTML);

      room = this._findRoom(payload.id);
    }

    if (!room) return;

    this.roomsTarget.insertBefore(room, this.roomsTarget.firstChild);
    this._setRoomActive(payload.id);
  }

  _updateRoomName(payload) {
    const room = this._findRoom(payload.id);
    if (!room) return;

    const nameEl = room.querySelector(this._sel('ROOM_NAME'));
    if (nameEl) {
      nameEl.textContent = payload.name;
    }

    this._markUnread(room);
  }

  _updateRoomLogo(payload) {
    const room = this._findRoom(payload.id);
    if (!room) return;

    const logoEl = room.querySelector(this._sel('ROOM_LOGO'));
    if (logoEl) {
      const newLogo = this._createElementFromHTML(this._buildRoomLogoHtml(payload));
      logoEl.replaceWith(newLogo);
    }

    this._markUnread(room);
  }

  _createUserMessage(payload) {
    let room = this._findRoom(payload.roomId);

    if (!room) {
      room = this._createNewRoom(payload);
    }

    if (!room) return;

    this._updateLastMessage(room, payload);
    this._moveToTopAndMarkUnread(room);
  }

  _updateUserMessage(payload) {
    const room = this._findRoom(payload.id);
    if (!room) return;

    const lastMessageEl = room.querySelector(this._sel('ROOM_LAST_MESSAGE'));
    if (lastMessageEl?.dataset.roomLastMessageId === payload.messageId.toString()) {
      lastMessageEl.textContent = `${payload.messageProfileTitle}: ${payload.messageContent.replaceAll('__everyone_label', this.everyoneLabelValue)}`;
    }
  }

  _deleteUserMessage(payload) {
    const room = this._findRoom(payload.id);
    if (!room) return;

    const lastMessageEl = room.querySelector(this._sel('ROOM_LAST_MESSAGE'));
    if (lastMessageEl?.dataset.roomLastMessageId === payload.messageId.toString()) {
      lastMessageEl.remove();
    }
  }

  _deleteRoom(payload) {
    const room = this._findRoom(payload.id);
    if (!room) return;

    room.remove();

    if (this.currentRoomId === payload.id) {
      this._handleCurrentRoomDeleted(payload);
    }
  }

  _markRoomAsRead(payload) {
    const room = this._findRoom(payload.id);
    if (room) {
      room.classList.remove(this._cls('ROOM_UNREAD'));
    }
  }

  changeRoom(event) {
    const roomId = event.currentTarget.dataset.roomId;

    this._setRoomActive(roomId);
  }

  _setRoomActive(roomId) {
    this._clearAllActiveRooms();

    const selectedRoom = this._findRoom(roomId);
    if (selectedRoom) {
      selectedRoom.classList.remove(this._cls('ROOM_UNREAD'));
      selectedRoom.classList.add(this._cls('ROOM_ACTIVE'));
    }

    this._updateCurrentUrl(roomId);
    this._setCurrentRoomId(roomId);
  }

  _findRoom(roomId) {
    return this.roomsTarget.querySelector(`[data-room-id="${roomId}"]`);
  }

  _clearAllActiveRooms() {
    this.roomsTarget.querySelectorAll(this._sel('ROOM'))
      .forEach(room => room.classList.remove(this._cls('ROOM_ACTIVE')));
  }

  _moveToTopAndMarkUnread(room) {
    this.roomsTarget.insertBefore(room, this.roomsTarget.firstChild);
    if (!room.classList.contains(this._cls('ROOM_ACTIVE'))) {
      room.classList.add(this._cls('ROOM_UNREAD'));
    }
  }

  _markUnread(room) {
    if (!room.classList.contains(this._cls('ROOM_ACTIVE'))) {
      room.classList.add(this._cls('ROOM_UNREAD'));
    }
  }

  _createNewRoom(payload) {
    const roomData = {
      id: payload.roomId,
      name: payload.roomName,
      backgroundColor: payload.roomBackgroundColor || '#cccccc',
      logoUrl: payload.roomLogoUrl,
      selected: false,
      unread: true
    };

    const roomHTML = this._buildRoomHTML(roomData);
    this.roomsTarget.insertAdjacentHTML('afterbegin', roomHTML);

    return this._findRoom(payload.roomId);
  }

  _updateLastMessage(room, payload) {
    let lastMessageEl = room.querySelector(this._sel('ROOM_LAST_MESSAGE'));
    const lastMessageContent = `${payload.lastMessageProfileTitle}: ${payload.lastMessageContent.replaceAll('__everyone_label', this.everyoneLabelValue)}`;

    if (lastMessageEl) {
      lastMessageEl.dataset.roomLastMessageId = payload.lastMessageId;
      lastMessageEl.textContent = lastMessageContent;
    } else {
      lastMessageEl = this._createLastMessageElement(payload.lastMessageId, lastMessageContent);
      const roomTitle = room.querySelector(this._sel('ROOM_TITLE'));
      roomTitle?.appendChild(lastMessageEl);
    }

    let lastMessageTimeEl = room.querySelector(this._sel('ROOM_LAST_MESSAGE_TIME'));
    if (lastMessageTimeEl) {
      lastMessageTimeEl.textContent = payload.lastMessageTime;
    } else {
      const nameEl = room.querySelector(this._sel('ROOM_NAME'));
      if (nameEl) {
        const timeEl = this._createLastMessageTimeElement(payload.lastMessageTime);
        nameEl.parentElement.appendChild(timeEl);
      }
    }
  }

  _createLastMessageElement(messageId, content) {
    const el = document.createElement('div');
    el.className = `${this._cls('ROOM_LAST_MESSAGE')} text-ellipsis truncate text-left text-xs`;
    el.dataset.roomLastMessageId = messageId;
    el.textContent = content;
    return el;
  }

  _createLastMessageTimeElement(time) {
    const el = document.createElement('div');
    el.className = `${this._cls('ROOM_LAST_MESSAGE_TIME')} ml-2 text-xs text-secondary`;
    el.textContent = time;
    return el;
  }

  _handleCurrentRoomDeleted(payload) {
    this._resetCurrentUrl();
    this._setCurrentRoomId(null);

    const roomContainer = document.getElementById(this.constructor.IDS.ROOM_CONTAINER);
    if (roomContainer) {
      roomContainer.innerHTML = this._buildEmptyRoomHTML(payload.title, payload.content);
    }
  }

  _createElementFromHTML(htmlString) {
    const template = document.createElement('template');
    template.innerHTML = htmlString.trim();
    return template.content.firstChild;
  }

  _setCurrentRoomId(currentRoomId) {
    this.currentRoomId = currentRoomId;
  }

  _resetCurrentUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('room_id');
    window.history.pushState({ path: url.href }, '', url.href);
  }

  _updateCurrentUrl(roomId) {
    const url = new URL(window.location.href);
    url.searchParams.set('room_id', roomId);
    window.history.pushState({ path: url.href }, '', url.href);
  }

  _buildRoomHTML(payload) {
    const classes = [this._cls('ROOM')];
    if (payload.selected) classes.push(this._cls('ROOM_ACTIVE'));
    if (!payload.selected && payload.unread) classes.push(this._cls('ROOM_UNREAD'));

    const logoStyle = payload.backgroundColor
      ? `style="background-color: ${this._escapeHtml(payload.backgroundColor)}"`
      : '';

    return `
      <form 
        data-action="click->rooms#changeRoom" 
        data-room-id="${this._escapeHtml(payload.id)}" 
        class="${classes.join(' ')}"
        method="get"
        action="/rooms/${this._escapeHtml(payload.id)}">
        <button 
          data-turbo-frame="room" 
          class="w-full flex items-center py-1 pl-1 pr-2 hover:bg-secondary-regular rounded select-none transition-colors" 
          type="submit">
          <div class="${this._cls('ROOM_LOGO')} h-12 rounded" ${logoStyle}>
            <div class="w-full h-full rounded overflow-hidden">
              <img class="object-cover object-center w-full h-full" src="${this._escapeHtml(payload.logoUrl)}" alt="${this._escapeHtml(payload.name)}">
            </div>
          </div>
          <div class="ml-2 ${this._cls('ROOM_TITLE')}">
            <div class="flex items-center">
              <div class="flex-1 ${this._cls('ROOM_NAME')} text-ellipsis truncate text-left">${this._escapeHtml(payload.name)}</div>
            </div>
          </div>
          <div class="${this._cls('ROOM_UNREAD_MARKER')}"></div>
        </button>
      </form>
    `;
  }

  _buildRoomLogoHtml(payload) {
    return `
      <div class="${this._cls('ROOM_LOGO')} h-12 rounded">
        <div class="w-full h-full rounded overflow-hidden">
          <img class="object-cover object-center w-full h-full" src="${this._escapeHtml(payload.roomLogoUrl)}" alt="Room logo">
        </div>
      </div>
    `;
  }

  _buildEmptyRoomHTML(title, content) {
    return `
      <div class="w-full h-full flex items-center justify-center flex-col">
        <i class="fa fa-sad-tear text-gray-400 text-4xl"></i>
        <div class="mt-4 font-bold text-gray-400 text-center">${this._escapeHtml(title)}</div>
        <div class="mt-2 px-12 text-gray-400 text-center">${this._escapeHtml(content)}</div>
      </div>
    `;
  }

  _escapeHtml(text) {
    if (text == null) return '';
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  }

  // Helper methods to convert CLASSES class names to selectors and vice versa
  _cls(key) {
    return this.constructor.CLASSES[key];
  }

  _sel(key) {
    return `.${this.constructor.CLASSES[key]}`;
  }
}